import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adminApi } from '../../../api/admin.api';
import { AdminVoteHistoryItem } from '../../../types/Admin.api.type';
import { Card, CardContent } from '../../ui/card';
import { Button } from '../../ui/button';

const PERIOD_OPTIONS = [
  { value: 'TODAY', label: '오늘' },
  { value: '7D', label: '7일' },
  { value: '30D', label: '30일' },
  { value: 'ALL', label: '전체' },
] as const;

/** 성별 한국어 매핑 */
const GENDER_LABELS: Record<string, string> = {
  MALE: '남성',
  FEMALE: '여성',
  UNKNOWN: '미설정',
};

/** 연령대 한국어 매핑 */
const AGE_GROUP_LABELS: Record<string, string> = {
  TEENS: '10대',
  TWENTIES: '20대',
  THIRTIES: '30대',
  FORTIES: '40대',
  FIFTIES: '50대',
  SIXTY_PLUS: '60대+',
  UNKNOWN: '미설정',
};

export const VoteHistoryPanel = () => {
  const [page, setPage] = useState(0);
  const [userInput, setUserInput] = useState('');
  const [userId, setUserId] = useState<number | undefined>(undefined);
  const [conversionPeriod, setConversionPeriod] = useState('TODAY');
  const size = 20;

  const { data, isLoading } = useQuery({
    queryKey: ['adminVoteHistory', page, userId],
    queryFn: () => adminApi.getVoteHistory(page, size, userId).then(res => res.data),
  });

  const { data: conversionStats } = useQuery({
    queryKey: ['voteConversionStats', conversionPeriod],
    queryFn: () => adminApi.getVoteConversionStats(conversionPeriod).then(res => res.data),
  });

  const handleSearch = () => {
    const parsed = parseInt(userInput, 10);
    setUserId(userInput ? (isNaN(parsed) ? undefined : parsed) : undefined);
    setPage(0);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  const formatDateTime = (dt: string) => {
    const d = new Date(dt);
    return `${(d.getMonth() + 1).toString().padStart(2, '0')}-${d.getDate().toString().padStart(2, '0')} ${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
  };

  return (
    <div className="space-y-4">
      {/* 투표 광고 전환율 */}
      <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-[#030213]">투표 광고 전환율</h3>
          <div className="flex gap-1">
            {PERIOD_OPTIONS.map(opt => (
              <button
                key={opt.value}
                onClick={() => setConversionPeriod(opt.value)}
                className={`px-2.5 py-1 text-xs rounded-md transition-colors ${
                  conversionPeriod === opt.value
                    ? 'bg-[#030213] text-white'
                    : 'bg-[#f3f3f5] text-[#6B7280] hover:bg-[#E5E7EB]'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
        {conversionStats ? (
          <div className="grid grid-cols-3 gap-4">
            <div>
              <p className="text-xs text-[#6B7280]">투표 수</p>
              <p className="text-lg font-bold text-[#030213]">{conversionStats.voteCount.toLocaleString()}</p>
            </div>
            <div>
              <p className="text-xs text-[#6B7280]">광고 시청</p>
              <p className="text-lg font-bold text-[#030213]">{conversionStats.adWatchCount.toLocaleString()}</p>
            </div>
            <div>
              <p className="text-xs text-[#6B7280]">전환율</p>
              <p className={`text-lg font-bold ${conversionStats.conversionRate >= 50 ? 'text-green-600' : conversionStats.conversionRate >= 30 ? 'text-yellow-600' : 'text-red-500'}`}>
                {conversionStats.conversionRate}%
              </p>
            </div>
          </div>
        ) : (
          <div className="text-sm text-[#6B7280]">로딩 중...</div>
        )}
      </div>

      {/* 필터 */}
      <div className="flex flex-wrap gap-2 items-end">
        <div>
          <label className="block text-xs text-gray-500 mb-1">유저 ID</label>
          <div className="flex gap-1">
            <input
              type="text"
              className="border rounded-md px-3 py-1.5 text-sm w-28"
              placeholder="유저 ID"
              value={userInput}
              onChange={e => setUserInput(e.target.value)}
              onKeyDown={handleKeyDown}
            />
            <Button size="sm" variant="outline" onClick={handleSearch}>
              검색
            </Button>
            {userId != null && (
              <Button size="sm" variant="ghost" onClick={() => { setUserInput(''); setUserId(undefined); setPage(0); }}>
                초기화
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* 데스크톱: 테이블 */}
      <Card className="overflow-hidden hidden md:block">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">시간</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">유저</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">설문 제목</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">선택지</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">성별</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">연령대</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {isLoading ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-gray-500">로딩 중...</td>
                  </tr>
                ) : (!data?.content || data.content.length === 0) ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-gray-500">데이터가 없습니다.</td>
                  </tr>
                ) : (
                  data.content.map((item: AdminVoteHistoryItem) => (
                    <tr key={item.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                        {formatDateTime(item.regDateTime)}
                      </td>
                      <td className="px-4 py-3">
                        {item.nickname ? (
                          <span className="text-gray-900">{item.nickname}</span>
                        ) : (
                          <span className="text-gray-400">(비회원)</span>
                        )}
                        {item.userId && (
                          <span className="text-xs text-gray-400 ml-1">#{item.userId}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-700 max-w-[250px] truncate" title={item.questionTitle}>
                        {item.questionTitle}
                      </td>
                      <td className="px-4 py-3 text-gray-600">{item.selectedOption}</td>
                      <td className="px-4 py-3">
                        {item.voterGender ? (
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                            item.voterGender === 'MALE' ? 'bg-blue-100 text-blue-700' :
                            item.voterGender === 'FEMALE' ? 'bg-pink-100 text-pink-700' :
                            'bg-gray-100 text-gray-600'
                          }`}>
                            {GENDER_LABELS[item.voterGender] ?? item.voterGender}
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        {item.voterAgeGroup ? (AGE_GROUP_LABELS[item.voterAgeGroup] ?? item.voterAgeGroup) : '-'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* 모바일: 카드 리스트 */}
      <div className="md:hidden space-y-3">
        {isLoading ? (
          <div className="py-8 text-center text-gray-500">로딩 중...</div>
        ) : (!data?.content || data.content.length === 0) ? (
          <div className="py-8 text-center text-gray-500">데이터가 없습니다.</div>
        ) : (
          data.content.map((item: AdminVoteHistoryItem) => (
            <Card key={item.id}>
              <div className="p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-900">
                    {item.nickname ?? '(비회원)'}
                    {item.userId && <span className="text-xs text-gray-400 ml-1">#{item.userId}</span>}
                  </span>
                  <div className="flex gap-1">
                    {item.voterGender && (
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                        item.voterGender === 'MALE' ? 'bg-blue-100 text-blue-700' :
                        item.voterGender === 'FEMALE' ? 'bg-pink-100 text-pink-700' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {GENDER_LABELS[item.voterGender] ?? item.voterGender}
                      </span>
                    )}
                    {item.voterAgeGroup && (
                      <span className="text-xs text-gray-500">
                        {AGE_GROUP_LABELS[item.voterAgeGroup] ?? item.voterAgeGroup}
                      </span>
                    )}
                  </div>
                </div>
                <div className="text-sm text-gray-700 truncate" title={item.questionTitle}>
                  {item.questionTitle}
                </div>
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span>선택: {item.selectedOption}</span>
                </div>
                <div className="text-xs text-gray-400">{formatDateTime(item.regDateTime)}</div>
              </div>
            </Card>
          ))
        )}
      </div>

      {/* 페이지네이션 */}
      {data && data.totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page === 0}
            onClick={() => setPage(p => p - 1)}
          >
            이전
          </Button>
          <span className="text-sm text-gray-600">
            {page + 1} / {data.totalPages} ({data.totalElements}건)
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= data.totalPages - 1}
            onClick={() => setPage(p => p + 1)}
          >
            다음
          </Button>
        </div>
      )}
    </div>
  );
};
